import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Trash2, History, Plus, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, BACKEND } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NoBizCta } from "@/pages/Journey";

const SUGGEST = ["Help me refine my target customer", "Challenge my business idea", "What questions should I ask in customer interviews?", "Analyse my offer and price", "Help me plan my first 30 days"];

function inline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") && p.length > 4 ? <strong key={i} className="font-bold">{p.slice(2, -2)}</strong> : p.replace(/\*\*/g, ""));
}

export function Markdown({ text }) {
  return (
    <div className="space-y-1.5">
      {text.split("\n").map((raw, i) => {
        const l = raw.trimEnd();
        if (!l.trim()) return <div key={i} className="h-1" />;
        const h = l.match(/^#{1,6}\s+(.*)/);
        if (h) return <p key={i} className="font-bold pt-1">{inline(h[1].replace(/\*\*/g, ""))}</p>;
        const b = l.match(/^\s*(?:[-*•]|\d+[.)])\s+(.*)/);
        if (b) {
          const num = l.match(/^\s*(\d+)[.)]/);
          return <div key={i} className="flex gap-2"><span className="shrink-0 font-semibold text-[#179B80]">{num ? `${num[1]}.` : "•"}</span><span>{inline(b[1])}</span></div>;
        }
        return <p key={i}>{inline(l)}</p>;
      })}
    </div>
  );
}

function Bubble({ m }) {
  const me = m.role === "user";
  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${me ? "bg-[#1E2B3C] text-white rounded-br-md whitespace-pre-wrap" : "bg-white border border-[#EADEC9] rounded-bl-md"}`} data-testid={`chat-msg-${m.role}`}>
        {m.content ? (me ? m.content : <Markdown text={m.content} />)
          : <span className="inline-flex gap-1"><span className="h-2 w-2 rounded-full bg-[#1DB99A] animate-bounce" /><span className="h-2 w-2 rounded-full bg-[#1DB99A] animate-bounce [animation-delay:.15s]" /><span className="h-2 w-2 rounded-full bg-[#1DB99A] animate-bounce [animation-delay:.3s]" /></span>}
      </div>
    </div>
  );
}

function HistorySheet({ open, onOpenChange, convs, activeId, onPick, onDelete, onNew }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-sm bg-[#FBF8F2] overflow-y-auto" data-testid="chat-history-sheet">
        <SheetHeader><SheetTitle className="font-display text-xl">Chat history</SheetTitle></SheetHeader>
        <button onClick={onNew} className="sb-btn-primary w-full mt-5 text-sm" data-testid="chat-history-new-btn"><Plus className="h-4 w-4" />New conversation</button>
        <ul className="mt-4 space-y-2" data-testid="chat-history-list">
          {!convs.length && <li className="text-sm text-[#8C9BA5]">No conversations yet.</li>}
          {convs.map((c) => (
            <li key={c.id} className={`flex items-center gap-2 rounded-xl border p-3 ${c.id === activeId ? "border-[#1DB99A] bg-[#E8F8F4]" : "border-[#EADEC9] bg-white"}`}>
              <button onClick={() => onPick(c.id)} className="flex-1 min-w-0 text-left" data-testid={`chat-history-item-${c.id}`}>
                <div className="text-sm font-semibold truncate flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5 shrink-0 text-[#1DB99A]" />{c.title}</div>
                <div className="text-[11px] text-[#8C9BA5] mt-0.5">{new Date(c.updated_at).toLocaleString()}</div>
              </button>
              <button onClick={() => onDelete(c.id)} className="h-8 w-8 rounded-full hover:bg-[#F5F0E6] flex items-center justify-center" aria-label="Delete" data-testid={`chat-history-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-[#8C9BA5]" /></button>
            </li>
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
}

export default function AICoach() {
  const { t } = useAuth();
  const [convs, setConvs] = useState(null);
  const [cid, setCid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [err, setErr] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const end = useRef(null);
  const loadConvs = () => api.get("/coach/conversations").then((r) => setConvs(r.data)).catch(() => setErr(true));
  useEffect(() => { loadConvs(); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  if (err) return <NoBizCta />;

  const pick = async (id) => {
    setShowHist(false);
    setCid(id);
    const { data } = await api.get(`/coach/conversations/${id}`);
    setMsgs(data);
  };
  const newChat = () => { setShowHist(false); setCid(null); setMsgs([]); };
  const remove = async (id) => {
    await api.delete(`/coach/conversations/${id}`);
    if (id === cid) newChat();
    loadConvs();
  };

  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${BACKEND}/api/coach/chat`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("sb_token")}` }, body: JSON.stringify({ message, conversation_id: cid }),
      });
      if (!res.ok) throw new Error("Coach unavailable");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const raw = p.replace(/^data: /, "");
          if (raw === "[DONE]") continue;
          const ev = JSON.parse(raw);
          if (ev.conversation_id) setCid(ev.conversation_id);
          if (ev.error) toast.error(ev.error);
          if (ev.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + ev.delta }; return c; });
        }
      }
      loadConvs();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-8.5rem)] md:h-[calc(100dvh-5rem)]" data-testid="ai-coach-page">
      <div className="flex items-center gap-2 mb-4">
        <span className="h-11 w-11 shrink-0 rounded-2xl bg-[#1E2B3C] flex items-center justify-center"><Sparkles className="h-5 w-5 text-[#1DB99A]" /></span>
        <div className="flex-1 min-w-0"><h1 className="text-xl font-extrabold">{t("Ask Sidebizz")}</h1><p className="text-xs text-[#5C6B73] truncate">Your thinking partner — not a lawyer, accountant or government authority.</p></div>
        <button onClick={newChat} className="h-10 w-10 rounded-full border border-[#EADEC9] bg-white flex items-center justify-center" aria-label="New chat" data-testid="chat-new-btn"><Plus className="h-4 w-4" /></button>
        <button onClick={() => setShowHist(true)} className="h-10 px-3 rounded-full border border-[#EADEC9] bg-white flex items-center gap-1.5 text-xs font-bold" data-testid="chat-history-btn">
          <History className="h-4 w-4" /><span className="hidden sm:inline">History</span>{convs?.length > 0 && <span className="text-[#179B80]">{convs.length}</span>}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="sb-card p-6 fade-up">
            <p className="text-sm text-[#5C6B73]">I know your business, current stage and saved answers. Try:</p>
            <div className="flex flex-wrap gap-2 mt-4">{SUGGEST.map((s, i) => <button key={s} onClick={() => send(s)} className="rounded-full border border-[#EADEC9] bg-[#FBF8F2] px-3 py-2 text-xs font-semibold hover:border-[#1DB99A]" data-testid={`chat-suggestion-${i}`}>{s}</button>)}</div>
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={i} m={m} />)}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask about your business…" data-testid="chat-input"
          className="flex-1 h-12 rounded-xl border border-[#EADEC9] bg-white px-4 text-base focus:outline-none focus:ring-2 focus:ring-[#1DB99A]" />
        <button disabled={busy || !text.trim()} className="sb-btn-primary w-12 px-0" data-testid="chat-send-btn"><Send className="h-4 w-4" /></button>
      </form>
      <HistorySheet open={showHist} onOpenChange={setShowHist} convs={convs || []} activeId={cid} onPick={pick} onDelete={remove} onNew={newChat} />
    </div>
  );
}
